import Label from 'atoms/Label';
import cn from 'classnames';
import Image, { ImageLoader } from 'next/image';
import React, { useMemo, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { ITag } from 'stores/interfaces';
import { IGalleryItem } from 'utils/globalTypes';
import { defaultLoader } from 'utils/images';
import { getValidUrl } from 'utils/urlUtils';

import s from './ImageGallery.module.scss';

interface IGallery {
  images?: Array<IGalleryItem>;
  tag?: ITag;
  logo?: string;
  className?: string;
  showBullets?: boolean;
  isPreviewMode?: boolean;
  image?: {
    loader?: ImageLoader;
    width?: string | number;
    height?: string | number;
    quality?: string | number;
    layout?: 'fill' | 'fixed' | 'intrinsic' | 'responsive';
  };
  onClick?: () => void;
}

const ImageGallery = ({
  images,
  tag,
  image,
  logo,
  className,
  showBullets,
  isPreviewMode,
  onClick
}: IGallery) => {
  const [showArrow, setShowArrow] = useState(false);

  const customNav = useMemo(
    () => (onClick: () => void, className: string) =>
      showArrow && (
        <div onClick={onClick} className={className}>
          <Image src="/svg/gallery-nav.svg" height={40} width={40} alt="gallery-icon-nav" />
        </div>
      ),
    [showArrow]
  );

  const logoUrl = useMemo(() => getValidUrl(logo), [logo]);

  const loader: ImageLoader | undefined = useMemo(
    () => (image?.loader ? image?.loader : ({ src }) => src),
    [image?.loader]
  );

  const validImages = useMemo(() => images?.filter((image) => image.original), [images]);

  return (
    <div
      className={cn(s.ImageGallery, { isPreviewMode }, className)}
      onMouseEnter={() => setShowArrow(!!(validImages && validImages?.length > 1))}
      onMouseLeave={() => setShowArrow(false)}
    >
      {tag?.label && (
        <Label {...tag} customStyle={s.ImageGallery__label}>
          {tag.label}
        </Label>
      )}
      {logoUrl && (
        <div className={s.ImageGallery__logoHolder}>
          <div className={s.ImageGallery__logoInnerHolder}>
            <Image src={logoUrl} alt="logo" fill className={s.ImageGallery__logo} priority={true} />
          </div>
        </div>
      )}
      <Carousel
        infiniteLoop
        statusFormatter={(current, total) => `${current}/${total}`}
        renderArrowNext={(onClick: () => void) =>
          customNav(onClick, 'ImageGallery__nav ImageGallery__nav_next')
        }
        renderArrowPrev={(onClick: () => void) =>
          customNav(onClick, 'ImageGallery__nav ImageGallery__nav_prev')
        }
        onClickItem={onClick}
        showIndicators={showBullets}
        className="ImageGallery__carouselWrapper"
        showThumbs={false}
        showStatus={false}
        showArrows={true}
        autoPlay={false}
      >
        {validImages?.map(({ original, title }, i) => (
          <Image
            className={s.ImageGallery__image}
            key={`image-${original}`}
            src={original ?? ''}
            width={Number(image?.width) ?? 393}
            height={Number(image?.height) ?? 263}
            quality={Number(image?.quality) ?? 75}
            loader={loader ?? defaultLoader}
            unoptimized={!image?.loader}
            priority={i < 5}
            alt={title || `Offering Image ${i}`}
          />
        ))}
      </Carousel>
    </div>
  );
};

export default ImageGallery;
